import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import {
    boxesApi,
    classesApi,
    imagesApi,
    projectsApi,
} from '../services/annotatorApi';

// Single message users see when they try to mutate a locked project. The
// backend would reject with 409 project_locked anyway; this short-circuits
// the optimistic UI update so changes don't appear to land + then snap
// back when the PATCH fails.
const LOCKED_TOAST = 'Project is locked. Unlock it to edit.';
import type {
    BoundingBox,
    Class as ClassModel,
    ImageMeta,
    ImageStatus,
    Project,
} from '../types/api';

interface UseAnnotatorStateArgs {
    orgId: string | undefined;
    projectId: string | undefined;
}

interface DraftBox {
    classIndex: number;
    x_center: number;
    y_center: number;
    width: number;
    height: number;
}

/**
 * Undo/redo entries — minimal create-only stack (Phase A.7.8 v1; full
 * command-pattern including update/delete is deferred per TODO.md).
 *
 * Phase B gate B.6 ("Undo 5 boxes, redo 3") only needs create-undo, which
 * is enough for the customer-facing draft-recovery story. Update/delete
 * undo is in the deferred list — extends the same stack with before/after
 * state per entry.
 */
type UndoEntry =
    | { kind: 'create'; box: BoundingBox }
    | { kind: 'delete'; box: BoundingBox };

/**
 * useAnnotatorState — central state machine for the annotation canvas.
 */
export function useAnnotatorState({ orgId, projectId }: UseAnnotatorStateArgs) {
    const [project, setProject] = useState<Project | null>(null);
    const [classes, setClasses] = useState<ClassModel[]>([]);
    const [images, setImages] = useState<ImageMeta[]>([]);
    const [activeImageId, setActiveImageId] = useState<string | null>(null);
    const [boxes, setBoxes] = useState<BoundingBox[]>([]);
    const [activeClassIndex, setActiveClassIndex] = useState<number>(0);
    // Image list filters — mutually exclusive. Picking a status clears
    // any class filter and vice versa. Server re-fetches the list each
    // time these change (effect below).
    const [statusFilter, setStatusFilter] = useState<ImageStatus | ''>('');
    const [classFilter, setClassFilter] = useState<number | null>(null);
    // G.6 — client-side filter on the per-image train/val/test split.
    // Only meaningful on a locked project; cleared on unlock.
    const [splitFilter, setSplitFilter] = useState<'train' | 'val' | 'test' | ''>('');
    const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
    const [selectedBoxIds, setSelectedBoxIds] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Undo/redo stacks — per active image. Reset on image switch.
    const undoStackRef = useRef<UndoEntry[]>([]);
    const redoStackRef = useRef<UndoEntry[]>([]);

    // Latest project state, read by mutators via ref so they don't go stale
    // mid-keypress / mid-drag. Mutators short-circuit when is_locked=true:
    // skip the optimistic UI write AND the API call, and toast the user so
    // the "nothing happened" feedback isn't silent. Defense in depth — the
    // backend's requireProjectUnlocked gate would 409 the request anyway,
    // but doing it here keeps the UI from showing changes that snap back.
    const projectRef = useRef<Project | null>(null);
    useEffect(() => { projectRef.current = project; }, [project]);
    const lastLockToastRef = useRef<number>(0);
    const blockedByLock = useCallback((): boolean => {
        if (!projectRef.current?.is_locked) return false;
        // Throttle the toast so rapid attempts (arrow-key auto-repeat, drag)
        // don't spawn dozens of duplicate banners.
        const now = Date.now();
        if (now - lastLockToastRef.current > 1500) {
            lastLockToastRef.current = now;
            toast.error(LOCKED_TOAST);
        }
        return true;
    }, []);

    // ---- Initial load (project + classes + images) ----
    useEffect(() => {
        if (!projectId) return;
        setLoading(true);
        Promise.all([
            projectsApi.get(projectId, orgId),
            classesApi.list(projectId, orgId),
            imagesApi.list(projectId, {}, orgId),
        ])
            .then(([p, cls, imgs]) => {
                setProject(p);
                setClasses(cls);
                setImages(imgs);
                if (imgs.length > 0 && !activeImageId) {
                    setActiveImageId(imgs[0].id);
                }
            })
            .catch((e) => setError(String(e?.message ?? e)))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId, projectId]);

    // ---- Re-fetch the image list whenever a filter changes -----------
    // Skip the very first run because the initial-load effect above
    // already fetched with empty filters. After that, any filter change
    // triggers a backend round-trip so the list stays in sync with the
    // server's view of which images match.
    const firstFilterRun = useRef(true);
    useEffect(() => {
        if (!projectId) return;
        if (firstFilterRun.current) { firstFilterRun.current = false; return; }
        imagesApi
            .list(projectId, {
                status: statusFilter || undefined,
                classIndex: classFilter ?? undefined,
            }, orgId)
            .then(setImages)
            .catch((e) => setError(String(e?.message ?? e)));
    }, [orgId, projectId, statusFilter, classFilter]);

    // ---- Load boxes when the active image changes ----
    useEffect(() => {
        if (!activeImageId) {
            setBoxes([]);
            return;
        }
        boxesApi.list(activeImageId, orgId).then(setBoxes).catch(() => setBoxes([]));
        setSelectedBoxId(null);
        setSelectedBoxIds(new Set());
        // Reset undo/redo stacks per image — undo history is per-image only.
        undoStackRef.current = [];
        redoStackRef.current = [];
    }, [orgId, activeImageId]);

    const activeImage = useMemo(
        () => images.find((i) => i.id === activeImageId) ?? null,
        [images, activeImageId],
    );

    const filteredImages = useMemo(() => {
        let out = images;
        if (statusFilter) {
            out = out.filter((i) => i.status === statusFilter);
        }
        // G.6 — split filter applies in-place. image.split is null on
        // unlocked projects so this is a no-op there.
        if (splitFilter) {
            out = out.filter((i) => i.split === splitFilter);
        }
        return out;
    }, [images, statusFilter, splitFilter]);

    // ---- Mutators ----

    const createBox = useCallback(
        async (draft: DraftBox): Promise<void> => {
            if (!activeImageId) return;
            if (blockedByLock()) return;
            const tempId = `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
            const tempBox: BoundingBox = {
                id: tempId,
                image_id: activeImageId,
                class_index: draft.classIndex,
                z_order: 0,
                x_center: draft.x_center,
                y_center: draft.y_center,
                width: draft.width,
                height: draft.height,
                created_at: new Date().toISOString(),
            };
            setBoxes((prev) => [...prev, tempBox]);
            try {
                const created = await boxesApi.create(
                    activeImageId,
                    {
                        class_index: draft.classIndex,
                        x_center: draft.x_center,
                        y_center: draft.y_center,
                        width: draft.width,
                        height: draft.height,
                    },
                    orgId,
                );
                setBoxes((prev) => prev.map((b) => (b.id === tempId ? created : b)));
                undoStackRef.current.push({ kind: 'create', box: created });
                redoStackRef.current = []; // user action invalidates redo
                // Mirror the backend's UpdateStatusOnFirstBox: as soon as
                // this image has its first box, the server flips status
                // pending → annotated + stamps annotated_at. Update the
                // local images array so the sidebar's status icon switches
                // from Clock → ✓ immediately, not on next refetch.
                setImages((prev) => prev.map((img) =>
                    img.id === activeImageId && img.status === 'pending'
                        ? { ...img, status: 'annotated', annotated_at: new Date().toISOString() }
                        : img,
                ));
            } catch (e) {
                setBoxes((prev) => prev.filter((b) => b.id !== tempId));
                setError(String((e as Error)?.message ?? e));
            }
        },
        [activeImageId, orgId],
    );

    // Save-state tracking — surfaces "Saving…" / "Saved 3s ago" indicators
    // in the UI so the user knows their edits are durable without having to
    // hunt for a save button.
    //
    //   pendingCount  — boxes whose debounce timer hasn't fired yet
    //   inFlightCount — PATCHes currently waiting on a backend response
    //   lastSavedAt   — last time a PATCH returned 2xx (ms epoch)
    //   lastSaveError — null when last save succeeded; error message otherwise
    const [pendingCount, setPendingCount] = useState(0);
    const [inFlightCount, setInFlightCount] = useState(0);
    const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
    const [lastSaveError, setLastSaveError] = useState<string | null>(null);

    const pendingPatchRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    // We also stash the LATEST patch per box so a Ctrl+S "flush now" can fire
    // the same payload the debounced send would have used.
    const pendingPatchPayloadRef = useRef<Map<string, Partial<BoundingBox>>>(new Map());

    const sendPatch = useCallback(async (id: string, patch: Partial<BoundingBox>) => {
        if (id.startsWith('temp-')) return;
        setInFlightCount((n) => n + 1);
        try {
            await boxesApi.update(id, patch, orgId);
            setLastSavedAt(Date.now());
            setLastSaveError(null);
        } catch (e) {
            const msg = String((e as Error)?.message ?? e);
            setError(msg);
            setLastSaveError(msg);
        } finally {
            setInFlightCount((n) => Math.max(0, n - 1));
        }
    }, [orgId]);

    const updateBox = useCallback(
        (id: string, patch: Partial<Omit<BoundingBox, 'id' | 'image_id' | 'created_at'>>) => {
            if (blockedByLock()) return;
            setBoxes((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
            const pending = pendingPatchRef.current;
            const existing = pending.get(id);
            if (existing) clearTimeout(existing);
            // Coalesce the patch — later edits merge into the same trailing PATCH.
            const prevPayload = pendingPatchPayloadRef.current.get(id) ?? {};
            pendingPatchPayloadRef.current.set(id, { ...prevPayload, ...patch });
            if (!existing) setPendingCount((n) => n + 1);
            // 1s debounce — long enough that arrow-key streaks (auto-repeat
            // ~30/s) and quick slider drags coalesce into ONE trailing PATCH;
            // short enough that a single edit feels durable within a beat.
            // Ctrl+S still flushes immediately for users who don't want to
            // wait. Previously 300ms — bumped after arrow-key feedback.
            const tid = setTimeout(() => {
                pending.delete(id);
                const payload = pendingPatchPayloadRef.current.get(id) ?? patch;
                pendingPatchPayloadRef.current.delete(id);
                setPendingCount((n) => Math.max(0, n - 1));
                void sendPatch(id, payload);
            }, 1000);
            pending.set(id, tid);
        },
        [sendPatch],
    );

    /**
     * nudgeBox — relative position delta in normalized YOLO coords.
     *
     * Why this exists instead of "compute new x/y at the call site and pass
     * to updateBox": when arrow keys auto-repeat, the global keyboard
     * handler fires faster than React's commit + closure refresh. Computing
     * `box.x_center + dx` at the call site reads a STALE box, producing the
     * same new value on every press → optimistic setBoxes becomes a no-op
     * → box appears stuck. Reading from boxesRef.current inside this hook
     * sees the post-previous-nudge state every time.
     *
     * Save behavior is unchanged from any other coord edit: optimistic UI
     * + 300ms debounced PATCH (coalesced — holding the arrow key for 2
     * seconds fires ONE trailing PATCH, not dozens).
     */
    const nudgeBox = useCallback((id: string, dxNormalized: number, dyNormalized: number) => {
        if (blockedByLock()) return;
        const box = boxesRef.current.find((b) => b.id === id);
        if (!box) return;
        const halfW = box.width / 2;
        const halfH = box.height / 2;
        const newX = Math.max(halfW, Math.min(1 - halfW, box.x_center + dxNormalized));
        const newY = Math.max(halfH, Math.min(1 - halfH, box.y_center + dyNormalized));
        if (newX === box.x_center && newY === box.y_center) return;
        updateBox(id, { x_center: newX, y_center: newY });
    }, [updateBox]);

    /**
     * Z-order helpers — one shape for ALL four operations (front/back/forward/
     * backward). Each builds the desired render-order list locally, then
     * sends the full list of box IDs to the server's reorder endpoint. The
     * server atomically assigns z_order = index in one transaction and
     * returns the canonical list — we replace local state with that response.
     *
     * Why this beats the per-box `z_order` PATCH approach:
     *   - One API call per click (was: up to N PATCHes for a renormalize)
     *   - No client/server race window — server is source of truth
     *   - No stale-closure bugs — reorder POST is atomic
     *   - Same primitive will support drag-reorder later (drop a box at any
     *     position → send the new list)
     */
    const reorderBoxes = useCallback(async (newIds: string[]) => {
        if (!activeImageId || newIds.length === 0) return;
        if (blockedByLock()) return;
        try {
            const updated = await boxesApi.reorder(activeImageId, newIds, orgId);
            setBoxes(updated);
            setLastSavedAt(Date.now());
        } catch (e) {
            setError(String((e as Error)?.message ?? e));
        }
    }, [activeImageId, orgId]);

    // Read latest box order from a ref so handlers don't capture stale
    // arrays across rapid clicks.
    const boxesRef = useRef<BoundingBox[]>(boxes);
    useEffect(() => { boxesRef.current = boxes; }, [boxes]);

    // One-step nudge — uses the O(1) swap endpoint (server figures out the
    // tied-z renormalization if needed). Wire size is two UUIDs no matter
    // how many boxes are on the image.
    //
    // CONCURRENCY: rapid clicks fire HTTP requests in parallel; HTTP/2
    // responses can land out of order, and the LATER `setBoxes(response)`
    // would clobber earlier writes with stale data. We serialize per-hook
    // by chaining each swap onto `swapQueueRef.current`. Each call only
    // starts after the previous resolves AND reads `boxesRef.current` at
    // that point — so it always sees the prior swap's response.
    const swapQueueRef = useRef<Promise<unknown>>(Promise.resolve());
    const moveOneStep = useCallback((id: string, direction: 'forward' | 'backward') => {
        if (blockedByLock()) return;
        const run = async () => {
            const current = boxesRef.current;
            const idx = current.findIndex((b) => b.id === id);
            if (idx < 0) return;
            const swapWith = direction === 'forward' ? idx + 1 : idx - 1;
            if (swapWith < 0 || swapWith >= current.length) return;
            const neighbor = current[swapWith];
            try {
                const updated = await boxesApi.swap(id, neighbor.id, orgId);
                setBoxes(updated);
                setLastSavedAt(Date.now());
            } catch (e) {
                setError(String((e as Error)?.message ?? e));
            }
        };
        // .then(run, run) runs the next swap whether the previous one
        // resolved or rejected — a single failed swap shouldn't deadlock
        // every subsequent click.
        swapQueueRef.current = swapQueueRef.current.then(run, run);
    }, [orgId]);

    const moveToEnd = useCallback((id: string, end: 'front' | 'back') => {
        const current = boxesRef.current;
        const idx = current.findIndex((b) => b.id === id);
        if (idx < 0) return;
        const without = current.filter((b) => b.id !== id);
        const reordered = end === 'front' ? [...without, current[idx]] : [current[idx], ...without];
        void reorderBoxes(reordered.map((b) => b.id));
    }, [reorderBoxes]);

    const bringBoxForward = useCallback((id: string) => moveOneStep(id, 'forward'), [moveOneStep]);
    const sendBoxBackward = useCallback((id: string) => moveOneStep(id, 'backward'), [moveOneStep]);
    const bringBoxToFront = useCallback((id: string) => moveToEnd(id, 'front'), [moveToEnd]);
    const sendBoxToBack = useCallback((id: string) => moveToEnd(id, 'back'), [moveToEnd]);

    /**
     * updateClassMeta — rename / recolor a class with optimistic UI + 409
     * rollback on duplicate-name. Throws on failure so the caller can
     * surface the message inline next to the row.
     */
    const updateClassMeta = useCallback(async (
        classIndex: number,
        patch: { name?: string; color?: string },
    ) => {
        if (!projectId) return;
        if (blockedByLock()) return;
        const before = classes;
        // Optimistic local update.
        setClasses((all) => all.map((c) => (c.class_index === classIndex ? { ...c, ...patch } : c)));
        try {
            const updated = await classesApi.update(projectId, classIndex, patch, orgId);
            setClasses((all) => all.map((c) => (c.class_index === classIndex ? updated : c)));
            setLastSavedAt(Date.now());
        } catch (e) {
            // Roll back local state if the PATCH fails (typically 409
            // class_name_taken on a rename collision).
            setClasses(before);
            throw e;
        }
    }, [classes, projectId, orgId]);

    /**
     * flushPendingSaves — fire all pending debounced PATCHes immediately.
     * Used by the Ctrl+S keyboard shortcut + the manual "Save" affordance
     * in the header save-state indicator.
     */
    const flushPendingSaves = useCallback(() => {
        const pending = pendingPatchRef.current;
        const payloads = pendingPatchPayloadRef.current;
        // Snapshot keys so we can mutate maps while iterating.
        const ids = Array.from(pending.keys());
        for (const id of ids) {
            const tid = pending.get(id);
            if (tid) clearTimeout(tid);
            pending.delete(id);
            const payload = payloads.get(id);
            payloads.delete(id);
            setPendingCount((n) => Math.max(0, n - 1));
            if (payload) void sendPatch(id, payload);
        }
    }, [sendPatch]);

    const deleteBox = useCallback(
        async (id: string, options?: { recordUndo?: boolean }) => {
            if (blockedByLock()) return;
            const recordUndo = options?.recordUndo !== false;
            const current = boxes.find((b) => b.id === id);
            setBoxes((prev) => prev.filter((b) => b.id !== id));
            setSelectedBoxId((curr) => (curr === id ? null : curr));
            setSelectedBoxIds((prev) => {
                if (!prev.has(id)) return prev;
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            if (id.startsWith('temp-')) return;
            try {
                await boxesApi.remove(id, orgId);
                if (recordUndo && current) {
                    undoStackRef.current.push({ kind: 'delete', box: current });
                    redoStackRef.current = [];
                }
            } catch (e) {
                setError(String((e as Error)?.message ?? e));
            }
        },
        [boxes, orgId],
    );

    /** Bulk delete — phase A.7.9. */
    const deleteSelected = useCallback(async () => {
        const ids = Array.from(selectedBoxIds);
        for (const id of ids) {
            await deleteBox(id, { recordUndo: true });
        }
        setSelectedBoxIds(new Set());
    }, [selectedBoxIds, deleteBox]);

    /** Toggle a box into the bulk-select set; pure UI state. */
    const toggleBoxInSelection = useCallback((id: string) => {
        setSelectedBoxIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    /** Undo most recent action on this image. */
    const undo = useCallback(async () => {
        if (blockedByLock()) return;
        const entry = undoStackRef.current.pop();
        if (!entry) return;
        if (entry.kind === 'create') {
            // Inverse: delete the box. Don't record this delete on undo stack.
            await deleteBox(entry.box.id, { recordUndo: false });
            redoStackRef.current.push(entry);
        } else if (entry.kind === 'delete') {
            // Inverse: re-create with same data; server will assign a new id.
            try {
                const created = await boxesApi.create(
                    entry.box.image_id,
                    {
                        class_index: entry.box.class_index,
                        x_center: entry.box.x_center,
                        y_center: entry.box.y_center,
                        width: entry.box.width,
                        height: entry.box.height,
                    },
                    orgId,
                );
                setBoxes((prev) => [...prev, created]);
                redoStackRef.current.push({ kind: 'delete', box: created });
            } catch (e) {
                setError(String((e as Error)?.message ?? e));
            }
        }
    }, [deleteBox, orgId]);

    /** Redo most recently undone action. */
    const redo = useCallback(async () => {
        if (blockedByLock()) return;
        const entry = redoStackRef.current.pop();
        if (!entry) return;
        if (entry.kind === 'create') {
            // Replay the create.
            try {
                const created = await boxesApi.create(
                    entry.box.image_id,
                    {
                        class_index: entry.box.class_index,
                        x_center: entry.box.x_center,
                        y_center: entry.box.y_center,
                        width: entry.box.width,
                        height: entry.box.height,
                    },
                    orgId,
                );
                setBoxes((prev) => [...prev, created]);
                undoStackRef.current.push({ kind: 'create', box: created });
            } catch (e) {
                setError(String((e as Error)?.message ?? e));
            }
        } else if (entry.kind === 'delete') {
            // Replay the delete.
            await deleteBox(entry.box.id, { recordUndo: true });
        }
    }, [deleteBox, orgId]);

    // ---- Image navigation ----

    const navigateImage = useCallback(
        (direction: -1 | 1) => {
            if (!activeImageId || filteredImages.length === 0) return;
            const idx = filteredImages.findIndex((i) => i.id === activeImageId);
            if (idx === -1) {
                setActiveImageId(filteredImages[0].id);
                return;
            }
            const nextIdx = (idx + direction + filteredImages.length) % filteredImages.length;
            setActiveImageId(filteredImages[nextIdx].id);
        },
        [activeImageId, filteredImages],
    );

    useEffect(() => {
        const pending = pendingPatchRef.current;
        return () => {
            pending.forEach((t) => clearTimeout(t));
            pending.clear();
        };
    }, []);

    return {
        project,
        setProject,
        classes,
        setClasses,
        images,
        setImages,
        filteredImages,
        statusFilter,
        setStatusFilter,
        // G.6 — exposed for the sidebar's filter dropdown. setSplitFilter
        // is bare (not Exclusive) because split is logically orthogonal
        // to status + class — a customer can want "only val images that
        // are annotated" without contradiction.
        splitFilter,
        setSplitFilter,
        activeImage,
        activeImageId,
        setActiveImageId,
        boxes,
        selectedBoxId,
        setSelectedBoxId,
        selectedBoxIds,
        toggleBoxInSelection,
        deleteSelected,
        activeClassIndex,
        setActiveClassIndex,
        loading,
        error,
        setError,
        createBox,
        updateBox,
        deleteBox,
        navigateImage,
        undo,
        redo,
        // Z-order helpers
        bringBoxToFront,
        sendBoxToBack,
        bringBoxForward,
        sendBoxBackward,
        // Class meta
        updateClassMeta,
        // Mutually-exclusive image filters — class clears status, status
        // clears class. Both null/empty = show all.
        classFilter,
        setStatusFilterExclusive: (s: ImageStatus | '') => {
            setStatusFilter(s);
            setClassFilter(null);
        },
        setClassFilterExclusive: (idx: number | null) => {
            setClassFilter(idx);
            setStatusFilter('');
        },
        // Arrow-key nudge — reads latest state from a ref so rapid key
        // presses don't compound on stale closures.
        nudgeBox,
        // Save-state surface
        pendingCount,
        inFlightCount,
        lastSavedAt,
        lastSaveError,
        flushPendingSaves,
    };
}
